import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";

export type Me = {
  id: string;
  email: string;
  fullName: string | null;
  source: string | null;
  mustChangePassword: boolean;
  admin: boolean;
};

export const meQueryKey = ["auth", "me"] as const;

/**
 * Loads the current session's profile via /api/auth/me. Returns null when the
 * user is not signed in (401), any other error rethrows.
 */
export async function fetchMe(): Promise<Me | null> {
  try {
    return await api.get<Me>("/api/auth/me");
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: fetchMe,
    staleTime: 60_000,
  });
}

export function useAuthActions() {
  const queryClient = useQueryClient();
  return {
    async logout() {
      await api.post("/api/auth/logout");
      queryClient.setQueryData(meQueryKey, null);
      await queryClient.cancelQueries();
      queryClient.clear();
    },
    async refreshMe() {
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  };
}
